// src/pages/document/DocumentSettingsPage.tsx
// Document settings page with tabs: Policies, Numbering, Categories, Templates, Page Defaults.

import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import {
  Card,
  CardContent,
  Alert,
  ErrorAlert,
} from '@react/components';
import { DocumentPolicyEditor } from '@/components/document/DocumentPolicyEditor';
import { PageSettingsEditor } from '@/components/document/PageSettingsEditor';

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

type SettingsTab = 'policies' | 'numbering' | 'categories' | 'templates' | 'pageDefaults';

const TABS: Array<{ key: SettingsTab; labelKey: string }> = [
  { key: 'policies', labelKey: 'documentSettings.tabs.policies' },
  { key: 'numbering', labelKey: 'documentSettings.tabs.numbering' },
  { key: 'categories', labelKey: 'documentSettings.tabs.categories' },
  { key: 'templates', labelKey: 'documentSettings.tabs.templates' },
  { key: 'pageDefaults', labelKey: 'documentSettings.tabs.pageDefaults' },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const DocumentSettingsPage = observer(() => {
  const store = useDocument();
  const scope = useScope();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<SettingsTab>('policies');
  const [saved, setSaved] = useState(false);

  const showSaved = useCallback(() => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }, []);

  const handleSavePolicy = useCallback(async (data: any) => {
    await store.CreateDocumentPolicy(data);
    showSaved();
  }, [store, scope.productId, showSaved]);

  const handleSavePageSettings = useCallback(async (_settings: any) => {
    // TODO: Save as tenant-level page defaults when API is available
    showSaved();
  }, [showSaved]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.settings')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('document.settings')}</h1>
      </div>

      {/* Success notification */}
      {saved && (
        <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>
          {t('document.saveSuccess') as string}
        </Alert>
      )}

      {/* Error state */}
      {store.error ? <ErrorAlert error={store.error as Error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Tab bar */}
      <div className="border-b border-border">
        <nav className="flex gap-6 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'policies' && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('documentSettings.policiesDesc')}</p>
          <DocumentPolicyEditor
            entityType="tenant"
            entityId={scope.tenantId}
            onSave={handleSavePolicy}
          />
        </div>
      )}

      {activeTab === 'numbering' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{t('documentSettings.numberingDesc')}</p>
            <Link
              to="/documents/numbering-schemes/new"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              {t('documentSettings.createScheme')}
            </Link>
          </div>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">{t('documentSettings.numberingManageHint')}</p>
              <Link
                to="/documents/numbering-schemes"
                className="text-sm text-primary hover:underline mt-2 inline-block"
              >
                {t('documentSettings.viewSchemes')}
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{t('documentSettings.categoriesDesc')}</p>
            <Link
              to="/documents/categories/new"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              {t('documentSettings.createCategory')}
            </Link>
          </div>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">{t('documentSettings.categoriesManageHint')}</p>
              <Link
                to="/documents/categories"
                className="text-sm text-primary hover:underline mt-2 inline-block"
              >
                {t('documentSettings.viewCategories')}
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{t('documentSettings.templatesDesc')}</p>
            <Link
              to="/documents/templates/new"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              {t('documentSettings.createTemplate')}
            </Link>
          </div>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">{t('documentSettings.templatesManageHint')}</p>
              <Link
                to="/documents/templates"
                className="text-sm text-primary hover:underline mt-2 inline-block"
              >
                {t('documentSettings.viewTemplates')}
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'pageDefaults' && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('documentSettings.pageDefaultsDesc')}</p>
          <PageSettingsEditor
            onSave={handleSavePageSettings}
          />
        </div>
      )}
    </div>
  );
});

export default DocumentSettingsPage;
